// ─── Queue types ───

export type QueueStatus = 'expected' | 'waiting' | 'called' | 'serving' | 'done';
export type QueueSource = 'walk-in' | 'appointment';
export type QueuePriority = 'normal' | 'urgent' | 'vip';

// ─── Helpers ───

export function toInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');
}

/**
 * Compute wait minutes using arrived_at (preferred) or created_at as fallback.
 * For expected entries (not yet arrived), returns 0.
 */
export function computeWaitMinutes(arrivedAt: string | null, createdAt: string | null): number {
  const ts = arrivedAt ?? createdAt;
  if (!ts) return 0;
  return Math.max(0, Math.round((Date.now() - new Date(ts).getTime()) / 60000));
}

export const STATUS_STYLES: Record<QueueStatus, { bg: string; color: string; label: string }> = {
  expected: { bg: '#f3e8ff', color: '#7c3aed', label: 'Expected' },
  waiting: { bg: '#fef3c7', color: '#92400e', label: 'Waiting' },
  called: { bg: '#dbeafe', color: '#1e40af', label: 'Called' },
  serving: { bg: '#d1fae5', color: '#065f46', label: 'Serving' },
  done: { bg: '#f1f5f9', color: '#64748b', label: 'Done' },
};

// ─── Reorder helpers ───

/**
 * Compute sequential position values (1, 2, 3, ...) for the given rows.
 * Returns tuples of [id, newPosition] for rows whose position changed.
 * Used after drag-reorder or when an item transitions to Done.
 */
export function normalizeActiveQueueOrder(
  activeRows: Array<{ id: string; position: number | null }>,
): Array<[id: string, newPosition: number]> {
  const updates: Array<[string, number]> = [];
  for (let i = 0; i < activeRows.length; i++) {
    const newPos = i + 1;
    if (activeRows[i].position !== newPos) {
      updates.push([activeRows[i].id, newPos]);
    }
  }
  return updates;
}

/**
 * Move an item from sourceIndex to destIndex within an array (in-place style).
 * Returns a new array with the item moved and sequential positions assigned.
 */
export function reorderActiveQueue<T extends { id: string; position: number | null }>(
  activeRows: T[],
  sourceIndex: number,
  destIndex: number,
): T[] {
  const result = [...activeRows];
  const [moved] = result.splice(sourceIndex, 1);
  result.splice(destIndex, 0, moved);
  return result;
}

// ─── Provider serving guard ───

/**
 * Check if a provider is already serving another queue entry today.
 * Used to warn before starting a second concurrent serving for the same provider.
 * Returns the existing serving entry if found, null otherwise.
 */
export function getActiveServingByProvider<
  T extends { id: string; status: string | null; provider_id: string | null },
>(queueItems: T[], providerId: string | null, excludeId: string): T | null {
  if (!providerId) return null;
  return (
    queueItems.find(
      (q) => q.id !== excludeId && q.status === 'serving' && q.provider_id === providerId,
    ) ?? null
  );
}

export const SUGGESTED_TAGS = [
  'First Visit',
  'VIP',
  'Elderly',
  'Wheelchair',
  'Interpreter Needed',
  'Special Care',
  'Cash Only',
  'Insurance',
];
